/**
 * Helper functions for translating filter names and values
 * Keeps internal values in English while displaying translated labels
 */

/**
 * Resolves `key` within `namespace`, falling back to `fallback` when the catalog
 * has no entry. next-intl echoes `<namespace>.<key>` for a miss and throws for a
 * malformed lookup; both cases yield `fallback`.
 */
export function translateOrFallback(
  t: (key: string) => string,
  namespace: string,
  key: string,
  fallback: string
): string {
  try {
    const translated = t(key);
    return translated.startsWith(`${namespace}.`) ? fallback : translated;
  } catch {
    return fallback;
  }
}

/**
 * Converts a human-readable label to a `specs` translation key.
 * Words split on whitespace, "/", "-" and "_"; "#", ".", ",", "(", ")" and
 * quotes are dropped, so punctuated labels yield keys next-intl can resolve
 * (a "." in a key would otherwise be read as a namespace separator).
 * Examples:
 * - "Fiber Brand" -> "fiberBrand"
 * - "Sq. Ft. per Carton" -> "sqFtPerCarton"
 * - "Tile/Plank Random Lengths" -> "tilePlankRandomLengths"
 * - "Durability (1-5)" -> "durability15"
 */
export function labelToTranslationKey(label: string): string {
  return label
    .replace(/[#.,()'"]/g, '')
    .trim()
    .split(/[\s/_-]+/)
    .filter(Boolean)
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');
}

/**
 * Converts a filter name to a camelCase key for translation lookup
 * Examples:
 * - "Fiber Brand" -> "fiberBrand"
 * - "Color Tones" -> "colorTones"
 * - "Type" -> "type"
 */
export function filterNameToCamelCase(name: string): string {
  return labelToTranslationKey(name);
}

/** `specs` key for a unit-of-measure suffix such as "Square Feet". */
export function getUnitTranslationKey(uom: string): string {
  return labelToTranslationKey(uom);
}

/**
 * Replaces the English unit suffix `uom` at the end of `label` with the
 * locale's translation when one exists ("12 Inches" -> "12 Polegadas").
 */
export function translateUnitSuffix(
  t: (key: string) => string,
  namespace: string,
  label: string,
  uom?: string
): string {
  if (!uom || !label.endsWith(` ${uom}`)) return label;
  const translated = translateOrFallback(
    t,
    namespace,
    getUnitTranslationKey(uom),
    uom
  );
  return `${label.slice(0, -uom.length)}${translated}`;
}

/**
 * Maps English filter names to translation keys
 * Used to translate filter labels while keeping values in English
 */
export function getFilterTranslationKey(filterName: string): string {
  const mapping: Record<string, string> = {
    'Global Filters': 'globalFilters',
    Brand: 'brand',
    'Color Tone': 'colorTone',
    'Color Tones': 'colorTones',
    'Color Variation': 'colorVariation',
    'Fiber Type': 'fiberType',
    'Fiber Brand': 'fiberBrand',
    'Face Weight': 'faceWeight',
    Application: 'application',
    Category: 'category',
    'Color Range': 'colorRange',
    Look: 'look',
    Species: 'species',
    Width: 'width',
    Thickness: 'thickness',
    Length: 'length',
    'Surface Type': 'surfaceType',
    'Surface Texture': 'surfaceTexture',
    'Shade Range': 'shadeRange',
    Material: 'material',
    'Installation Method': 'installationMethod',
    Type: 'type',
    Line: 'line',
    Collection: 'collection',
    Color: 'color',
    Samples: 'samples',
    Visualizable: 'visualizable',
    Favorites: 'favorites',
    'Pet Friendly': 'petFriendly',
    Price: 'price',
    Tags: 'tags',
    'Edge Profile': 'edgeProfile',
  };

  // Return mapped value or convert to camelCase as fallback
  return mapping[filterName] || filterNameToCamelCase(filterName);
}

/**
 * Converts a spec key name to a camelCase key for translation lookup
 * Examples:
 * - "Product Line" -> "productLine"
 * - "Fiber Type" -> "fiberType"
 * - "SKU #" -> "sku"
 */
export function specKeyToCamelCase(key: string): string {
  return labelToTranslationKey(key);
}

/**
 * Maps English spec keys to translation keys
 * Used to translate spec labels in ProductSpec component
 */
export function getSpecTranslationKey(specKey: string): string {
  const mapping: Record<string, string> = {
    Brand: 'brand',
    'Product Line': 'productLine',
    Color: 'color',
    'SKU #': 'sku',
    Application: 'application',
    Category: 'category',
    'Fiber Type': 'fiberType',
    'Face Weight': 'faceWeight',
    'Pattern Repeat Width': 'patternRepeatWidth',
    'Pattern Repeat Length': 'patternRepeatLength',
    Inches: 'inches',
    Warranty: 'warranty',
    Construction: 'construction',
    Backing: 'backing',
    Width: 'width',
    Length: 'length',
    Thickness: 'thickness',
    Wear: 'wear',
    'Wear Layer': 'wearLayer',
    Finish: 'finish',
    Installation: 'installation',
    Species: 'species',
    Grade: 'grade',
    Edge: 'edge',
    Dimensions: 'dimensions',
    'Square Feet Per Box': 'squareFeetPerBox',
    'Pieces Per Box': 'piecesPerBox',
    Weight: 'weight',
    Residential: 'residential',
    Commercial: 'commercial',
  };

  // Return mapped value or convert to camelCase as fallback
  return mapping[specKey] || specKeyToCamelCase(specKey);
}
